export const NAV_LINKS = [
  { label: 'Features', href: '#features' },
  { label: 'Games', href: '#games' },
  { label: 'Pricing', href: '#pricing' },
  { label: 'Testimonials', href: '#testimonials' },
];

export interface Feature {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

export const FEATURES: Feature[] = [
  {
    id: 'qr-campaigns',
    title: 'QR Code Campaigns',
    description: 'Create trackable QR campaigns with validation controls. Monitor scans, conversions, and performance in real time.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
      </svg>
    ),
    color: 'text-blue-500 bg-blue-50',
  },
  {
    id: 'games',
    title: 'Interactive Mini-Games',
    description: 'Engage customers with a curated library of mini‑games—mix quick challenges, puzzles, and instant-win moments without the casino vibe.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-purple-500 bg-purple-50',
  },
  {
    id: 'loyalty',
    title: 'Loyalty Points Engine',
    description: 'Reward customers with points from gameplay. Build loyalty programs with a rewards catalog, redemption tracking, and progression.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    color: 'text-pink-500 bg-pink-50',
  },
  {
    id: 'leaderboards',
    title: 'Leaderboards & Achievements',
    description: 'Drive repeat visits with leaderboards and achievements. Unlock badges and challenge milestones to keep customers coming back.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
      </svg>
    ),
    color: 'text-yellow-500 bg-yellow-50',
  },
  {
    id: 'analytics',
    title: 'Real-Time Analytics',
    description: 'Understand what’s working with campaign and gameplay analytics. Segment customers by engagement and behavior.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    ),
    color: 'text-green-500 bg-green-50',
  },
  {
    id: 'data-collection',
    title: 'First-Party Data Capture',
    description: 'Collect customer names, phones, and Instagram handles through fun gameplay. GDPR-compliant with privacy-first design.',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    color: 'text-indigo-500 bg-indigo-50',
  },
];

export interface Game {
  id: string;
  name: string;
  description: string;
  category: string;
  icon: string;
  difficulty: string;
  avgEngagement: string;
}

export const GAMES: Game[] = [
  {
    id: 'spin-win',
    name: 'Spin & Win',
    description: 'A lightweight prize wheel that feels playful and brand‑safe. Great for quick rewards and instant delight.',
    category: 'Instant reward',
    icon: '🎡',
    difficulty: 'Easy',
    avgEngagement: 'High engagement'
  },
  {
    id: 'color-match',
    name: 'Color Match',
    description: 'A quick attention challenge—tap the label, not the color. Great for brand recall and quick wins.',
    category: 'Skill',
    icon: '🎨',
    difficulty: 'Medium',
    avgEngagement: 'High engagement'
  },
  {
    id: 'lucky-dice',
    name: 'Lucky Dice',
    description: 'A playful dice roll that keeps it light and fun. Great for quick points and repeat participation.',
    category: 'Instant reward',
    icon: '🎲',
    difficulty: 'Easy',
    avgEngagement: 'Steady engagement'
  },
  {
    id: 'memory-match',
    name: 'Memory Match',
    description: 'A cozy memory game that’s easy to understand and fun to replay.',
    category: 'Puzzle',
    icon: '🧠',
    difficulty: 'Easy',
    avgEngagement: 'Steady engagement'
  },
  {
    id: 'quick-tap',
    name: 'Quick Tap',
    description: 'A fast reflex challenge that’s great for events, queues, and on-the-go moments.',
    category: 'Speed',
    icon: '👆',
    difficulty: 'Hard',
    avgEngagement: 'High engagement'
  },
  {
    id: 'word-puzzle',
    name: 'Word Puzzle',
    description: 'A word challenge that rewards curiosity and keeps customers thinking about your brand.',
    category: 'Puzzle',
    icon: '📚',
    difficulty: 'Medium',
    avgEngagement: 'Steady engagement'
  }
];

export const TESTIMONIALS = [
  {
    id: 1,
    name: "Sarah Chen",
    role: "Marketing Director",
    company: "Bloom Boutique",
    content: "We placed QR codes on receipts and customers actually scanned. The mini‑game made signups feel effortless, and we captured far more contacts than with our usual promos.",
    avatar: "https://i.pravatar.cc/150?u=a042581f4e29026024d",
    metric: "Stronger scan‑to‑signup"
  },
  {
    id: 2,
    name: "Michael Rodriguez",
    role: "Owner",
    company: "Urban Cafe & Bar",
    content: "The games fit our brand and the leaderboard keeps people coming back. It feels more like community than marketing—and the engagement shows.",
    avatar: "https://i.pravatar.cc/150?u=a042581f4e29026704d",
    metric: "More repeat visits"
  },
  {
    id: 3,
    name: "Emily Watson",
    role: "Head of Growth",
    company: "FitZone Gym",
    content: "The analytics helped us see which experiences convert best so we could refine the campaign fast. It’s one of our most reliable lead channels now.",
    avatar: "https://i.pravatar.cc/150?u=a042581f4e29026024d",
    metric: "Clearer campaign ROI"
  }
];

export interface PricingTier {
  name: string;
  monthlyPrice: number | null;
  annualPrice: number | null;
  period?: string;
  description: string;
  features: string[];
  cta: string;
  popular?: boolean;
}

export const PRICING_TIERS: PricingTier[] = [
  {
    name: 'Starter',
    monthlyPrice: 0,
    annualPrice: 0,
    period: '/month',
    description: 'Perfect for small businesses testing gamification',
    features: [
      'Starter QR scan allowance',
      'Multiple campaigns',
      'All games included',
      'Advanced analytics & reporting',
      'Customer data export',
      'Email support'
    ],
    cta: 'Get Started for Free'
  },
  {
    name: 'Professional',
    monthlyPrice: 16.90,
    annualPrice: 13.52,
    period: '/month',
    description: 'For growing businesses scaling engagement',
    features: [
      'Higher QR scan allowance',
      'Unlimited campaigns',
      'All games included',
      'Advanced analytics & reporting',
      'Customer data export',
      'Email priority support',

    ],
    cta: 'Get Started',
    popular: true
  },
  {
    name: 'Enterprise',
    monthlyPrice: null,
    annualPrice: null,
    period: '/month',
    description: 'For large organizations with custom needs',
    features: [
      'Unlimited QR scans',
      'All games included + custom games',
      'White-label solution',
      'Dedicated account manager',
      'Custom integrations (API)',
      'Multi-location support',
      'SLA guarantee',
      'Advanced security & compliance'
    ],
    cta: 'Contact Sales'
  }
];

export const USE_CASES = [
  {
    industry: "Retail Stores",
    description: "Place QR codes on packaging, receipts, or in-store displays. Customers scan, play, and you capture their data for loyalty programs.",
    metric: "Collect more first‑party data",
    icon: "🛍️"
  },
  {
    industry: "Restaurants & Bars",
    description: "Table tents, menus, or receipts with QR codes. Gamify the dining experience and build a customer database for promotions.",
    metric: "Increase repeat customers",
    icon: "🍽️"
  },
  {
    industry: "Events & Trade Shows",
    description: "Booth QR codes for instant engagement. Collect leads through gameplay instead of boring forms. Export data to your CRM.",
    metric: "More booth engagement",
    icon: "🎪"
  },
  {
    industry: "E-Commerce Brands",
    description: "Include QR codes in product packaging. Drive social media follows through Instagram integration and leaderboard sharing.",
    metric: "Grow your social following",
    icon: "📦"
  },
  {
    industry: "Gyms & Fitness",
    description: "QR codes on equipment or membership cards. Gamify fitness challenges and build community through leaderboards.",
    metric: "Increase member engagement",
    icon: "💪"
  },
  {
    industry: "Coffee Shops",
    description: "Cup sleeves or loyalty cards with QR codes. Replace punch cards with digital points and instant-win games.",
    metric: "More loyalty signups",
    icon: "☕"
  }
];
